import React, { useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity,
  StyleSheet, Alert, TextInput, ActivityIndicator,
} from 'react-native';
import { useAuth } from '../context/AuthContext';
import { vehiclesAPI } from '../services/api';

export default function ProfileScreen({ navigation }) {
  const { user, logout } = useAuth();
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vForm, setVForm] = useState({
    modelo: '', ano: '', placa: '', categoria: 'carro',
    preco_diario: '', cidade_base: '', capacidade_carga: '',
  });

  const updV = (k, v) => setVForm(f => ({ ...f, [k]: v }));

  const handleLogout = () => {
    Alert.alert('Sair', 'Deseja sair da sua conta?', [
      { text: 'Cancelar', style: 'cancel' },
      { text: 'Sair', style: 'destructive', onPress: logout },
    ]);
  };

  const handleAddVehicle = async () => {
    if (!vForm.modelo || !vForm.placa || !vForm.preco_diario || !vForm.cidade_base) {
      return Alert.alert('Atenção', 'Preencha modelo, placa, preço e cidade base');
    }
    setLoading(true);
    try {
      await vehiclesAPI.cadastrar({
        ...vForm,
        ano: parseInt(vForm.ano) || new Date().getFullYear(),
        preco_diario: parseFloat(vForm.preco_diario),
        capacidade_carga: vForm.capacidade_carga ? parseFloat(vForm.capacidade_carga) : null,
      });
      Alert.alert('✅ Veículo cadastrado!', 'Seu veículo já está disponível na plataforma.');
      setShowAddVehicle(false);
      setVForm({ modelo: '', ano: '', placa: '', categoria: 'carro', preco_diario: '', cidade_base: '', capacidade_carga: '' });
    } catch (err) {
      Alert.alert('Erro', err.message);
    } finally {
      setLoading(false);
    }
  };

  const CatBtn = ({ value, label }) => (
    <TouchableOpacity
      style={[styles.catBtn, vForm.categoria === value && styles.catBtnActive]}
      onPress={() => updV('categoria', value)}
    >
      <Text style={[styles.catBtnText, vForm.categoria === value && styles.catBtnTextActive]}>{label}</Text>
    </TouchableOpacity>
  );

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* Cabeçalho perfil */}
      <View style={styles.header}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>{user?.nome?.[0]?.toUpperCase() || '?'}</Text>
        </View>
        <Text style={styles.userName}>{user?.nome}</Text>
        <Text style={styles.userEmail}>{user?.email}</Text>
        <View style={styles.tipoBadge}>
          <Text style={styles.tipoBadgeText}>
            {user?.tipo_usuario === 'locador' ? '🔑 Locador' : user?.tipo_usuario === 'ambos' ? '🔄 Locador & Locatário' : '🚗 Locatário'}
          </Text>
        </View>
      </View>

      {/* Dados da conta */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Dados da Conta</Text>
        <InfoRow icon="📞" label="Telefone"  value={user?.telefone || '—'} />
        <InfoRow icon="🪪" label="Documento" value={user?.documento || '—'} />
        <InfoRow icon="📅" label="Membro desde" value={user?.criado_em ? new Date(user.criado_em).toLocaleDateString('pt-BR') : '—'} />
      </View>

      {/* Cadastrar veículo (somente locadores) */}
      {(user?.tipo_usuario === 'locador' || user?.tipo_usuario === 'ambos') && (
        <View style={styles.section}>
          <TouchableOpacity
            style={styles.addVehicleHeader}
            onPress={() => setShowAddVehicle(!showAddVehicle)}
          >
            <Text style={styles.sectionTitle}>Cadastrar Veículo</Text>
            <Text style={styles.expandIcon}>{showAddVehicle ? '▲' : '▼'}</Text>
          </TouchableOpacity>

          {showAddVehicle && (
            <View style={{ marginTop: 8 }}>
              <Text style={styles.label}>Tipo de veículo</Text>
              <View style={styles.catRow}>
                <CatBtn value="carro"      label="🚗 Carro" />
                <CatBtn value="van"        label="🚐 Van" />
                <CatBtn value="caminhao_b" label="🚚 Caminhão B" />
              </View>

              <Text style={styles.label}>Modelo *</Text>
              <TextInput style={styles.input} placeholder="Ex: Volkswagen Gol" value={vForm.modelo} onChangeText={v => updV('modelo', v)} />

              <Text style={styles.label}>Ano</Text>
              <TextInput style={styles.input} placeholder="2022" keyboardType="numeric" value={vForm.ano} onChangeText={v => updV('ano', v)} />

              <Text style={styles.label}>Placa *</Text>
              <TextInput style={styles.input} placeholder="ABC-1234" autoCapitalize="characters" value={vForm.placa} onChangeText={v => updV('placa', v)} />

              <Text style={styles.label}>Cidade base *</Text>
              <TextInput style={styles.input} placeholder="Ex: Chapecó" value={vForm.cidade_base} onChangeText={v => updV('cidade_base', v)} />

              <Text style={styles.label}>Preço por dia (R$) *</Text>
              <TextInput style={styles.input} placeholder="120.00" keyboardType="decimal-pad" value={vForm.preco_diario} onChangeText={v => updV('preco_diario', v)} />

              {(vForm.categoria === 'van' || vForm.categoria === 'caminhao_b') && (
                <>
                  <Text style={styles.label}>Capacidade de carga (kg)</Text>
                  <TextInput style={styles.input} placeholder="1500" keyboardType="numeric" value={vForm.capacidade_carga} onChangeText={v => updV('capacidade_carga', v)} />
                </>
              )}

              <TouchableOpacity
                style={[styles.btnAdd, loading && { opacity: 0.6 }]}
                onPress={handleAddVehicle}
                disabled={loading}
              >
                {loading
                  ? <ActivityIndicator color="#1a1a1a" />
                  : <Text style={styles.btnAddText}>Cadastrar Veículo</Text>
                }
              </TouchableOpacity>
            </View>
          )}
        </View>
      )}

      {/* Sobre o YBR */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Sobre o YBR</Text>
        <Text style={styles.aboutText}>
          Yellow Brick Road é uma plataforma colaborativa P2P de locação de veículos para trajetos de mão única.{'\n\n'}
          Conectamos locatários e locadores, permitindo que veículos retornem à cidade de origem sem custo extra — e ambos economizam! 🚗💛
        </Text>
        <Text style={styles.aboutVersion}>Versão 1.0.0 · TCC UNOESC Chapecó 2025</Text>
      </View>

      {/* Sair */}
      <TouchableOpacity style={styles.btnLogout} onPress={handleLogout}>
        <Text style={styles.btnLogoutText}>Sair da conta</Text>
      </TouchableOpacity>

      <View style={{ height: 40 }} />
    </ScrollView>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <View style={styles.infoRow}>
      <Text style={styles.infoIcon}>{icon}</Text>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue}>{value}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container:        { flex: 1, backgroundColor: '#f9f9f9' },
  header:           { backgroundColor: '#F5C518', paddingTop: 56, paddingBottom: 28, alignItems: 'center' },
  avatar:           { width: 72, height: 72, borderRadius: 36, backgroundColor: '#1a1a1a', alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  avatarText:       { fontSize: 32, fontWeight: 'bold', color: '#F5C518' },
  userName:         { fontSize: 22, fontWeight: 'bold', color: '#1a1a1a' },
  userEmail:        { fontSize: 14, color: '#444', marginTop: 2 },
  tipoBadge:        { backgroundColor: '#1a1a1a', borderRadius: 12, paddingHorizontal: 14, paddingVertical: 6, marginTop: 10 },
  tipoBadgeText:    { color: '#F5C518', fontWeight: '600', fontSize: 13 },
  section:          { backgroundColor: '#fff', margin: 12, borderRadius: 16, padding: 18, shadowColor: '#000', shadowOpacity: 0.05, shadowRadius: 6, elevation: 2 },
  sectionTitle:     { fontSize: 16, fontWeight: 'bold', color: '#1a1a1a', marginBottom: 14 },
  infoRow:          { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  infoIcon:         { fontSize: 18, width: 32 },
  infoLabel:        { fontSize: 13, color: '#888', width: 110 },
  infoValue:        { fontSize: 14, fontWeight: '600', color: '#1a1a1a', flex: 1 },
  addVehicleHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  expandIcon:       { fontSize: 16, color: '#888' },
  label:            { fontSize: 13, fontWeight: '600', color: '#555', marginBottom: 6, marginTop: 12 },
  input:            { backgroundColor: '#f5f5f5', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 12, fontSize: 15, color: '#1a1a1a' },
  catRow:           { flexDirection: 'row', gap: 8 },
  catBtn:           { flex: 1, paddingVertical: 10, backgroundColor: '#f5f5f5', borderRadius: 10, alignItems: 'center' },
  catBtnActive:     { backgroundColor: '#F5C518' },
  catBtnText:       { fontSize: 12, fontWeight: '600', color: '#666' },
  catBtnTextActive: { color: '#1a1a1a' },
  btnAdd:           { backgroundColor: '#F5C518', borderRadius: 12, paddingVertical: 14, alignItems: 'center', marginTop: 16 },
  btnAddText:       { fontSize: 15, fontWeight: 'bold', color: '#1a1a1a' },
  aboutText:        { fontSize: 14, color: '#666', lineHeight: 22, marginBottom: 10 },
  aboutVersion:     { fontSize: 12, color: '#bbb', textAlign: 'center' },
  btnLogout:        { margin: 12, borderRadius: 14, paddingVertical: 14, alignItems: 'center', borderWidth: 1.5, borderColor: '#ef4444' },
  btnLogoutText:    { color: '#ef4444', fontWeight: 'bold', fontSize: 15 },
});
